import { InjectionToken, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { GATEWAY_URL } from './api.config';
import { isSessionRevoked } from './auth-session';

/** Opens a socket.io connection; tests provide a fake. */
export type SocketFactory = (origin: string, options: Partial<ManagerOptions & SocketOptions>) => Socket;

export const SESSION_SOCKET_FACTORY = new InjectionToken<SocketFactory>('SESSION_SOCKET_FACTORY', {
  providedIn: 'root',
  factory: () => io,
});

/**
 * Instant sign-out for one session in this tab.
 *
 * While the tab is signed in, it keeps a socket to the messaging service
 * (through the gateway, at `${GATEWAY_URL}/messaging/socket.io`). When a
 * sign-in from another browser or device replaces the session, the service
 * sends `session_revoked` at once, instead of the tab finding out on its next
 * request (the 401 `session_revoked` the interceptor handles).
 */
export class SessionSocket {
  private socket: Socket | null = null;
  private refreshedForReconnect = false;

  constructor(
    private readonly zone: NgZone,
    private readonly open: SocketFactory,
    private readonly accessToken: () => string | undefined,
    private readonly refresh: () => Observable<unknown>,
    private readonly revoked: () => void,
  ) {}

  connect(): void {
    if (this.socket || !this.accessToken()) {
      return;
    }
    const { origin, path } = socketEndpoint(GATEWAY_URL);
    // Outside Angular: the socket's heartbeats shouldn't trigger change detection.
    this.zone.runOutsideAngular(() => {
      const socket = this.open(origin, {
        path,
        // Read at every (re)connect, so a reconnect presents the current token.
        auth: (cb) => cb({ token: this.accessToken() }),
      });
      socket.on('session_revoked', () => this.zone.run(() => this.revoked()));
      socket.on('connect', () => (this.refreshedForReconnect = false));
      // Reconnecting after the access token expired is refused: refresh once, retry.
      socket.on('connect_error', (err) => {
        if (err.message !== 'unauthorized' || this.refreshedForReconnect) {
          return;
        }
        this.refreshedForReconnect = true;
        this.zone.run(() =>
          this.refresh().subscribe({
            next: () => this.socket?.connect(),
            error: (e) => isSessionRevoked(e) && this.revoked(),
          }),
        );
      });
      this.socket = socket;
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }
}

/** Where the socket lives behind the gateway: `${gatewayUrl}/messaging/socket.io`. */
export function socketEndpoint(gatewayUrl: string): { origin: string; path: string } {
  const url = new URL(gatewayUrl);
  return { origin: url.origin, path: `${url.pathname.replace(/\/+$/, '')}/messaging/socket.io` };
}
